import { useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { useTheme } from '@chakra-ui/react'
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Text,
  Heading,
  Flex,
  Image,
  Stack
} from '@homebotapp/hb-react-component-catalog'
import assumablesHomeIcon from '../../../../../../assets/homes/img/assumable-home-icon.png'
import { BuyDownDirectMessage } from './BuyDownDirectMessage'
import { useCreateClientEvent } from '../../../../../../hooks'
import { ClientEventName, ClientEventSource } from '../../../../../../api/gqlaxy/gql/generated/graphql'

const MSG = defineMessages({
  learnMore: {
    id: 'BuyDownLearnMoreModal.learnMore',
    defaultMessage: 'Learn more'
  },
  learnMoreAbout: {
    id: 'BuyDownLearnMoreModal.learnMoreAbout',
    defaultMessage: 'LEARN MORE ABOUT'
  },
  buyDowns: {
    id: 'BuyDownLearnMoreModal.buyDowns',
    defaultMessage: 'Buydowns'
  },
  info: {
    id: 'BuyDownLearnMoreModal.info',
    defaultMessage: 'Info'
  },
  whatIsBuydown: {
    id: 'BuyDownLearnMoreModal.whatIsBuydown',
    defaultMessage: 'What is a buydown?'
  },
  temporaryBuydown: {
    id: 'BuyDownLearnMoreModal.temporaryBuydown',
    defaultMessage: 'temporary buydown'
  },
  whatIsBuydownBodyPreface: {
    id: 'BuyDownLearnMoreModal.whatIsBuydownBodyPreface',
    defaultMessage: 'A '
  },
  whatIsBuydownBody1: {
    id: 'BuyDownLearnMoreModal.whatIsBuydownBody1',
    defaultMessage:
      ' is an arrangement where someone (usually a home seller or builder) subsidizes part of the loan such that the borrower enjoys lower monthly payments for the first 1–3 years of the loan. After this initial period, the loan reverts to the full interest rate.'
  },
  whatIsBuydownBody2: {
    id: 'BuyDownLearnMoreModal.whatIsBuydownBody2',
    defaultMessage:
      'Buydowns commonly appear as seller concessions to make a home more attractive to buyers. They can also be negotiated during the offer process.'
  },
  whatIsBuydownBody3: {
    id: 'BuyDownLearnMoreModal.whatIsBuydownBody3',
    defaultMessage:
      'Temporary buydowns can be great options for buyers who want to ease into their full monthly payments.'
  },
  whatTypes: {
    id: 'BuyDownLearnMoreModal.whatTypes',
    defaultMessage: 'What types of buydowns are there?'
  },
  threeTwoOne: {
    id: 'BuyDownLearnMoreModal.threeTwoOne',
    defaultMessage: '3-2-1 Buydown'
  },
  threeTwoOneYear1: {
    id: 'BuyDownLearnMoreModal.threeTwoOneYear1',
    defaultMessage: 'Year 1: Interest rate is reduced by 3%'
  },
  threeTwoOneYear2: {
    id: 'BuyDownLearnMoreModal.threeTwoOneYear2',
    defaultMessage: 'Year 2: Interest rate is reduced by 2%'
  },
  threeTwoOneYear3: {
    id: 'BuyDownLearnMoreModal.threeTwoOneYear3',
    defaultMessage: 'Year 3: Interest rate is reduced by 1%'
  },
  threeTwoOneYear4: {
    id: 'BuyDownLearnMoreModal.threeTwoOneYear4',
    defaultMessage: 'Year 4 and beyond: Full interest rate applies'
  },
  twoOne: {
    id: 'BuyDownLearnMoreModal.twoOne',
    defaultMessage: '2-1 Buydown'
  },
  mostCommon: {
    id: 'BuyDownLearnMoreModal.mostCommon',
    defaultMessage: '(most common)'
  },
  twoOneYear1: {
    id: 'BuyDownLearnMoreModal.twoOneYear1',
    defaultMessage: 'Year 1: Interest rate is reduced by 2%'
  },
  twoOneYear2: {
    id: 'BuyDownLearnMoreModal.twoOneYear2',
    defaultMessage: 'Year 2: Interest rate is reduced by 1%'
  },
  twoOneYear3: {
    id: 'BuyDownLearnMoreModal.twoOneYear3',
    defaultMessage: 'Year 3 and beyond: Full interest rate applies'
  },
  oneOne: {
    id: 'BuyDownLearnMoreModal.oneOne',
    defaultMessage: '1-1 Buydown'
  },
  oneOneYear1: {
    id: 'BuyDownLearnMoreModal.oneOneYear1',
    defaultMessage: 'Years 1 and 2: Interest rate is reduced by 1%'
  },
  oneOneYear2: {
    id: 'BuyDownLearnMoreModal.oneOneYear2',
    defaultMessage: 'Year 3 and beyond: Full interest rate applies'
  },
  oneZero: {
    id: 'BuyDownLearnMoreModal.oneZero',
    defaultMessage: '1-0 Buydown'
  },
  oneZeroYear1: {
    id: 'BuyDownLearnMoreModal.oneZeroYear1',
    defaultMessage: 'Year 1: Interest rate is reduced by 1%'
  },
  oneZeroYear2: {
    id: 'BuyDownLearnMoreModal.oneZeroYear2',
    defaultMessage: 'Year 2 and beyond: Full interest rate applies'
  },
  whatIfRefi: {
    id: 'BuyDownLearnMoreModal.whatIfRefi',
    defaultMessage: 'What happens if I refinance?'
  },
  whatIfRefiBody: {
    id: 'BuyDownLearnMoreModal.whatIfRefiBody',
    defaultMessage:
      'If you refinance during the temporary buydown period, the remaining balance in the buydown escrow fund (used to subsidize payments) is usually applied to pay down your loan principal or credited back to you, depending on your loan terms and lender policies.'
  },
  askChristopher: {
    id: 'BuyDownLearnMoreModal.askChristopher',
    defaultMessage: 'Ask Christopher about buydowns'
  },
  whatIsBuydownBody1Rest: {
    id: 'BuyDownLearnMoreModal.whatIsBuydownBody1Rest',
    defaultMessage:
      ' is an arrangement where someone (usually a home seller or builder) subsidizes part of the loan such that the borrower enjoys lower monthly payments for the first 1–3 years of the loan. After this initial period, the loan reverts to the full interest rate.'
  },
  close: {
    id: 'BuyDownLearnMoreModal.close',
    defaultMessage: 'Close'
  },
  buyDownDetailsCTA: {
    id: 'BuyDownLearnMoreModal.buyDownDetailsCTA',
    defaultMessage: 'Learn more about buydowns'
  }
})

export const BuyDownLearnMoreModal = ({
  detailPage = false,
  listingId = null
}: {
  detailPage?: boolean
  listingId?: string
}) => {
  const intl = useIntl()
  const { theme } = useTheme()
  const [showModal, setShowModal] = useState(false)

  const { mutateAsync: createClientEvent } = useCreateClientEvent()

  return (
    <>
      <Button
        variant='ghost'
        colorScheme='primary'
        size='xs'
        onClick={() => {
          setShowModal(true)
          createClientEvent({
            clientEventInput: {
              eventName: ClientEventName.OpenedBuyDownInfo,
              eventSource: listingId ? ClientEventSource.ListingDetailed : ClientEventSource.Calculators,
              ...(listingId && { listingId })
            }
          })
        }}
        tracking={{
          guid: 'tgoA1Qsu8dnLZUqE8rWN2K',
          ui_context: 'Buy Down Learn More Modal',
          descriptor: 'Learn More About Buy Downs'
        }}
      >
        {detailPage ? intl.formatMessage(MSG.buyDownDetailsCTA) : intl.formatMessage(MSG.info)}
      </Button>

      <Modal isOpen={showModal} size={'full'} onClose={() => setShowModal(false)}>
        <ModalOverlay />
        <ModalContent overflow='hidden' borderRadius={16} marginInline={[0, 5, '20%']} marginTop={[0, 6]} mb={[0, 8]}>
          <ModalHeader
            backgroundSize='cover'
            backgroundBlendMode='lighten'
            _dark={{
              backgroundColor: 'blackAlpha.600'
            }}
          >
            <Flex>
              <Image src={assumablesHomeIcon} aria-hidden='true' h={20} marginLeft={-6} />
              <Box ml={4}>
                <Text size={'md'} mb={0}>
                  {intl.formatMessage(MSG.learnMoreAbout)}
                </Text>
                <Heading as={'h3'} size={'lg'} textAlign={'left'}>
                  {intl.formatMessage(MSG.buyDowns)}
                </Heading>
              </Box>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box mt={4} mb={7}>
              <Heading as='h4' size='md' mb={3}>
                {intl.formatMessage(MSG.whatIsBuydown)}
              </Heading>
              <Text size='sm' mb={3}>
                {intl.formatMessage(MSG.whatIsBuydownBodyPreface)}
                <Text as='span' fontWeight='bold' display='inline' mx={1}>
                  {intl.formatMessage(MSG.temporaryBuydown)}
                </Text>
                {intl.formatMessage(MSG.whatIsBuydownBody1Rest)}
              </Text>
              <Text size='sm' mb={3}>
                {intl.formatMessage(MSG.whatIsBuydownBody2)}
              </Text>
              <Text size='sm'>{intl.formatMessage(MSG.whatIsBuydownBody3)}</Text>
            </Box>
            <Box borderTop='1px solid' borderColor='neutral.700' my={6} />
            <Box mb={7}>
              <Heading as='h4' size='md' mb={3}>
                {intl.formatMessage(MSG.whatTypes)}
              </Heading>
              <Box mb={3}>
                <Text as='b' size='sm'>
                  {intl.formatMessage(MSG.threeTwoOne)}
                </Text>
                <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 20 }}>
                  <li>
                    <Text size='sm'>{intl.formatMessage(MSG.threeTwoOneYear1)}</Text>
                  </li>
                  <li>
                    <Text size='sm'>{intl.formatMessage(MSG.threeTwoOneYear2)}</Text>
                  </li>
                  <li>
                    <Text size='sm'>{intl.formatMessage(MSG.threeTwoOneYear3)}</Text>
                  </li>
                  <li>
                    <Text size='sm'>{intl.formatMessage(MSG.threeTwoOneYear4)}</Text>
                  </li>
                </ul>
              </Box>
              <Box mb={3}>
                <Text as='b' size='sm'>
                  {intl.formatMessage(MSG.twoOne)}{' '}
                  <Text as='span' color='success.600'>
                    {intl.formatMessage(MSG.mostCommon)}
                  </Text>
                </Text>
                <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 20 }}>
                  <li>
                    <Text size='sm'>{intl.formatMessage(MSG.twoOneYear1)}</Text>
                  </li>
                  <li>
                    <Text size='sm'>{intl.formatMessage(MSG.twoOneYear2)}</Text>
                  </li>
                  <li>
                    <Text size='sm'>{intl.formatMessage(MSG.twoOneYear3)}</Text>
                  </li>
                </ul>
              </Box>
              <Box mb={3}>
                <Text as='b' size='sm'>
                  {intl.formatMessage(MSG.oneOne)}
                </Text>
                <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 20 }}>
                  <li>
                    <Text size='sm'>{intl.formatMessage(MSG.oneOneYear1)}</Text>
                  </li>
                  <li>
                    <Text size='sm'>{intl.formatMessage(MSG.oneOneYear2)}</Text>
                  </li>
                </ul>
              </Box>
              <Box mb={3}>
                <Text as='b' size='sm'>
                  {intl.formatMessage(MSG.oneZero)}
                </Text>
                <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 20 }}>
                  <li>
                    <Text size='sm'>{intl.formatMessage(MSG.oneZeroYear1)}</Text>
                  </li>
                  <li>
                    <Text size='sm'>{intl.formatMessage(MSG.oneZeroYear2)}</Text>
                  </li>
                </ul>
              </Box>
            </Box>
            <Box borderTop='1px solid' borderColor='neutral.700' my={6} />
            <Box mb={7}>
              <Heading as='h4' size='md' mb={3}>
                {intl.formatMessage(MSG.whatIfRefi)}
              </Heading>
              <Text size='sm'>{intl.formatMessage(MSG.whatIfRefiBody)}</Text>
            </Box>
            <Stack dir='column' spacing={3} align='center' textAlign={'center'} mb={4}>
              <BuyDownDirectMessage listingId={listingId} />
              <Button variant='link' colorScheme='primary' onClick={() => setShowModal(false)}>
                {intl.formatMessage(MSG.close)}
              </Button>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
